import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import MapModal, { MapSizeInvalidator } from '@pages/DockagePage/components/MapModal.tsx';

import styles from './DockagesMap.module.scss';

export type PointDto = { id: string; lat: number; lng: number; name?: string; address?: string };

export type MapCoords = { lat: number; lng: number };

export type RadiusSearchParams = MapCoords & { radiusMiles: number };

const MILES_TO_METERS = 1609.34;

function ClickHandler({
  onPick,
  onMapClick,
}: {
  onPick: (lat: number, lng: number) => void;
  onMapClick?: () => void;
}) {
  useMapEvents({
    click(e) {
      onMapClick?.();
      onPick(e?.latlng?.lat, e?.latlng?.lng);
    },
  });
  return null;
}

function MapCenterUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  const centerKey = `${center[0]},${center[1]}`;

  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, centerKey, map]);

  return null;
}

export function RadiusSearchMap({
  points,
  radiusMiles = 10,
  initialCenter,
  searchCenter,
  onSearch,
}: {
  points: PointDto[];
  radiusMiles?: number;
  initialCenter?: { lat: number; lng: number } | null;
  searchCenter?: { lat: number; lng: number } | null;
  onSearch?: (params: { lat: number; lng: number; radiusMiles: number }) => void;
}) {
  const radiusMeters = radiusMiles * MILES_TO_METERS;
  const [activePointId, setActivePointId] = useState<string | null>(null);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const markerIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).toString(),
        iconRetinaUrl: new URL(
          'leaflet/dist/images/marker-icon-2x.png',
          import.meta.url,
        ).toString(),
        shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).toString(),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      }),
    [],
  );

  const defaultCenter: [number, number] = [40.7128, -74.006];

  // ponytail: drop points missing coords or at 0,0 (Null Island) — no real location
  const validPoints = points?.filter(
    (p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng) && (p.lat !== 0 || p.lng !== 0),
  );

  const center: [number, number] = searchCenter
    ? [searchCenter.lat, searchCenter.lng]
    : initialCenter
      ? [initialCenter.lat, initialCenter.lng]
      : validPoints?.length
        ? [validPoints[0].lat, validPoints[0].lng]
        : defaultCenter;

  /* A record page draws this map to show where one place is, with no search
     behind it — clicking it there must do nothing rather than throw. */
  const handlePick = (lat: number, lng: number) => {
    onSearch?.({ lat, lng, radiusMiles });
  };

  return (
    <>
      <MapContainer center={center} zoom={12} zoomControl={false} className={styles.mapContainer}>
        <>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoomControl position="bottomright" />
          <MapSizeInvalidator />
          <MapCenterUpdater center={center} />
          <ClickHandler onPick={handlePick} onMapClick={() => setIsMapOpen(true)} />

          {searchCenter && (
            <>
              <Marker position={[searchCenter.lat, searchCenter.lng]} icon={markerIcon} />
              <Circle center={[searchCenter.lat, searchCenter.lng]} radius={radiusMeters} />
            </>
          )}

          {validPoints?.map((p) => (
            <Marker
              key={p?.id}
              position={[p?.lat, p?.lng]}
              icon={markerIcon}
              eventHandlers={{
                click: (event) => {
                  L.DomEvent.stopPropagation(event.originalEvent);
                  setActivePointId(p.id);
                  handlePick(p.lat, p.lng);
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -32]}
                opacity={1}
                permanent={activePointId === p?.id}
              >
                {p?.address || p?.name || `${p?.lat?.toFixed(4)}, ${p?.lng?.toFixed(4)}`}
              </Tooltip>
            </Marker>
          ))}
        </>
      </MapContainer>
      {isMapOpen && (
        <MapModal
          open={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          onMapClick={() => setActivePointId(null)}
          center={center}
          onSearch={onSearch}
          radiusMiles={radiusMiles}
          searchCenter={searchCenter}
          validPoints={validPoints}
          markerIcon={markerIcon}
          radiusMeters={radiusMeters}
          setActivePointId={setActivePointId}
          activePointId={activePointId}
        />
      )}
    </>
  );
}
